import './Generate.scss';
import {
  Box,
  Typography,
  RadioGroup,
  Radio,
  FormControlLabel,
} from '@mui/material';
import {t} from 'i18next';
import {useForm} from 'react-hook-form';
import {
  ProductionGenerateFormData,
  productionGenerateSchema,
} from '../../../../schemas/productions/productionSchema';
import {zodResolver} from '@hookform/resolvers/zod';
import {
  useEffect,
  forwardRef,
  useImperativeHandle,
  useState,
  useRef,
  useMemo,
} from 'react';
import ArrButton from '../../../common/arr-button/ArrButton';
import ArrModal from '../../../common/arr-modal/ArrModal';
import {ReactComponent as RadioActiveIcon} from '../../../../assets/images/radio-active-icon.svg';
import {ReactComponent as RadioEnableIcon} from '../../../../assets/images/radio-enable-icon.svg';
import {
  useGetGenerateView,
  useExecuteGenerate,
  useExecutePlaceholdering,
  useGetGenerateStatus,
  useGetExportBatesRange,
} from '../../../../hooks/productions/useProductions';
import {
  GetGenerateViewRequestParam,
  ExecuteGenerateRequestBody,
  ExecutePlaceholderingRequestBody,
  GetGenerateStatusRequestParam,
  GetExportBatesRangeRequestParam,
} from '../../../../api/types/production.type';
import {
  RegenerateType,
  ProductionStatus,
  ProductionType,
  ModuleType,
  ProductionState,
  FormAction,
  ToastType,
} from '../CreateNewProductionUtils';
import {
  removeParenthesisSpaces,
  parseStatusText,
} from '../CreateNewProductionHelpers';
import ErrorGridModal from './ErrorModal/ErrorGridModal';
import {ErrorGridType} from './ErrorModal/utils';

// Component Constants
// Generate/Production constants
const DEFAULT_BATES_RANGE_PLACEHOLDER = '---';
const DEFAULT_POLLING_INTERVAL_MS = 5000;
const GENERATE_VIEW_UPDATE_DELAY_MS = 1000;
const STATUS_CHECK_DELAY_MS = 1500;

export interface StepChildRef {
  save: () => void;
  next: () => void;
}

interface GenerateProps {
  securityGroupId: number;
  productionSetId?: number;
  productionId: number;
  isProductionSet: boolean;
  getStatus: (showToast: boolean, status: ToastType, message?: string) => void;
  goNext?: () => void;
  goBack?: () => void;
  onStateChange?: (state: {
    saveDisabled: boolean;
    nextDisabled: boolean;
  }) => void;
  productionStatusCode?: string;
}

const Generate = forwardRef<StepChildRef, GenerateProps>(
  (
    {
      securityGroupId = 0,
      productionId = 0,
      isProductionSet,
      getStatus,
      goNext,
      onStateChange,
      productionStatusCode,
    },
    ref
  ) => {
    // ---------- Local state ----------
    const [isPolling, setIsPolling] = useState(false);
    const [isOperationInProgress, setIsOperationInProgress] = useState(false);
    const pollingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(
      null
    );
    const statusCheckTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
      null
    );
    const lastProcessedStatusRef = useRef<string | null>(null);
    const [showRegenerateModal, setShowRegenerateModal] = useState(false);
    const [selectedRegenerateType, setSelectedRegenerateType] =
      useState<RegenerateType>(RegenerateType.RegenerateAll);
    const [showPlaceholderErrorDocsModal, setShowPlaceholderErrorDocsModal] =
      useState(false);
    const [openErrorGridModal, setOpenErrorGridModal] = useState(false);
    const [gridType, setGridType] = useState<ErrorGridType>(
      ErrorGridType.ProductionErrors
    );

    // ---------- Form ----------
    const {
      handleSubmit,
      setValue,
      formState: {isSubmitting, isValid},
    } = useForm<ProductionGenerateFormData>({
      mode: 'onChange',
      resolver: zodResolver(productionGenerateSchema()),
      defaultValues: {
        action: FormAction.NEXT,
      },
    });

    // Determine moduleType from isProductionSet using enum
    const moduleType = useMemo(
      () => (isProductionSet ? ModuleType.PRODUCTION : ModuleType.EXPORT),
      [isProductionSet]
    );

    const isGenerateViewEnabled = productionId > 0 && !isPolling;

    const generateViewParams = useMemo<GetGenerateViewRequestParam>(
      () => ({
        moduletype: moduleType,
        SecurityGroupId: securityGroupId,
        productionId,
      }),
      [moduleType, securityGroupId, productionId]
    );

    const {data: generateViewData} = useGetGenerateView(
      generateViewParams,
      isGenerateViewEnabled
    );

    const statusParams = useMemo<GetGenerateStatusRequestParam>(
      () => ({
        SecurityGroupId: securityGroupId,
        moduletype: moduleType,
        productionId,
      }),
      [moduleType, securityGroupId, productionId]
    );

    const {data: statusData, refetch: refetchStatus} = useGetGenerateStatus(
      statusParams,
      isPolling && productionId > 0
    );

    const executeGenerateMutation = useExecuteGenerate(
      (data) => {
        if (data.message === 'Cancelled') {
          getStatus?.(
            true,
            ToastType.Error,
            t(
              'productionsAndExports.createNewProduction.generate.cancelProgress'
            )
          );
          return;
        }
        getStatus?.(true, ToastType.Success, data.message || '');
        setIsOperationInProgress(true);
        setIsPolling(true);
      },
      (error) => {
        getStatus?.(true, ToastType.Error, error.message || '');
        setIsOperationInProgress(false);
        setIsPolling(false);
      }
    );

    const executePlaceholderingMutation = useExecutePlaceholdering(
      (data) => {
        getStatus?.(true, ToastType.Success, data.messageForUser || '');
        setIsOperationInProgress(true);
        setIsPolling(true);
      },
      (error) => {
        getStatus?.(true, ToastType.Error, error.message || '');
        setIsOperationInProgress(false);
        setIsPolling(false);
      }
    );

    const productionGenerateDetails =
      generateViewData?.productionGenerateDetails;

    // Use status from getGenerateStatus API during polling, otherwise use getGenerateView data
    const currentStatus = statusData?.productionCurrentStatus;
    const isCancelCompleted =
      currentStatus?.productionState === ProductionState.CANCELCOMPLETED ||
      productionGenerateDetails?.productionStateCode ===
        ProductionState.CANCELCOMPLETED;
    const rawStatusText =
      isPolling && currentStatus?.productionStatusText
        ? currentStatus.productionStatusText
        : productionGenerateDetails?.productionGenerateStatus ||
          t('productionsAndExports.createNewProduction.generate.draftStatus');
    const displayStatus = isCancelCompleted
      ? t('productionsAndExports.createNewProduction.generate.draftStatus')
      : rawStatusText;
    const displayBatesRange = isPolling
      ? currentStatus?.generatedBatesRange || DEFAULT_BATES_RANGE_PLACEHOLDER
      : productionGenerateDetails?.generatedBatesRange ||
        DEFAULT_BATES_RANGE_PLACEHOLDER;
    const producitonDocsCompleteText = removeParenthesisSpaces(
      currentStatus?.producitonDocsComplete
    );
    const parsedStatus = parseStatusText(displayStatus);

    const liveStatus =
      statusData?.productionCurrentStatus?.productionStatus ??
      productionGenerateDetails?.productionStatusCode1 ??
      productionStatusCode;

    // consider latest production state (from polling status first, then generate view)
    const latestProductionState =
      statusData?.productionCurrentStatus?.productionState ??
      productionGenerateDetails?.productionStateCode;

    // disable regenerate only if status is INPROGRESS or COMPLETED or CANCELFAILED and NOT QC complete
    const isInProgressOrCompleted =
      (liveStatus === ProductionStatus.COMPLETED ||
        latestProductionState == ProductionState.CANCELFAILED ||
        liveStatus === ProductionStatus.INPROGRESS) &&
      latestProductionState !== ProductionState.QCCOMPLETE;

    // UI booleans (kept same as original)
    const canShowPlaceholderErrorDocsButton =
      productionGenerateDetails?.canContinueWithPlacholdering ?? false;
    const canShowGenerateButton =
      productionGenerateDetails?.canGenerate ?? false;
    const canShowRegenerateButton =
      productionGenerateDetails?.canRegenerate ?? false;
    const shouldContinueGeneration =
      productionGenerateDetails?.shouldContinue ?? false;
    const canShowExportBatesButton =
      !!displayBatesRange &&
      displayBatesRange !== DEFAULT_BATES_RANGE_PLACEHOLDER;
    const canShowNativelyPlaceholderErrorDocs =
      (productionGenerateDetails?.nativelyGeneratedDocs ?? 0) > 0;
    const batesRange = displayBatesRange;

    // Automated checks mapping (kept same)
    const automatedChecks = [
      {
        name: productionGenerateDetails?.automatedCheckStep1Name,
        status: productionGenerateDetails?.automatedCheckStep1Status,
        className: productionGenerateDetails?.automatedCheckStep1ClassName,
      },
      {
        name: productionGenerateDetails?.automatedCheckStep2Name,
        status: productionGenerateDetails?.automatedCheckStep2Status,
        className: productionGenerateDetails?.automatedCheckStep2ClassName,
      },
      {
        name: productionGenerateDetails?.automatedCheckStep3Name,
        status: productionGenerateDetails?.automatedCheckStep3Status,
        className: productionGenerateDetails?.automatedCheckStep3ClassName,
      },
      {
        name: productionGenerateDetails?.automatedCheckStep4Name,
        status: productionGenerateDetails?.automatedCheckStep4Status,
        className: productionGenerateDetails?.automatedCheckStep4ClassName,
      },
      {
        name: productionGenerateDetails?.automatedCheckStep5Name,
        status: productionGenerateDetails?.automatedCheckStep5Status,
        className: productionGenerateDetails?.automatedCheckStep5ClassName,
      },
    ].filter((c) => c.name);

    // Expose methods to parent
    useImperativeHandle(ref, () => ({
      save: () => {},
      next: () => {
        if (isInProgressOrCompleted) {
          goNext?.();
          return;
        }

        setValue('action', FormAction.NEXT);
        handleSubmit(onSubmit)();
      },
    }));

    // polling interval value (ms)
    const pollingInterval =
      generateViewData?.productionGeneratePollingInterval ||
      DEFAULT_POLLING_INTERVAL_MS;

    // If a production is already in progress on load, auto-start polling (unless backend says 'shouldContinue')
    useEffect(() => {
      const details = generateViewData?.productionGenerateDetails;
      if (!details || productionId <= 0) return;

      // do not auto-start if backend requires user action to continue
      if (details.shouldContinue) return;

      if (
        details.productionStatusCode1 === ProductionStatus.INPROGRESS &&
        details.productionStateCode !== ProductionState.QCCOMPLETE
      ) {
        setIsPolling(true);
      }
    }, [generateViewData, productionId]);

    // ------- Polling effect: start/stop the interval and cleanup -------
    useEffect(() => {
      if (!isPolling || productionId <= 0) {
        // ensure cleared if polling turned off
        /* istanbul ignore next */
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
        // Clear timeout and reset processed status ref
        /* istanbul ignore next */
        if (statusCheckTimeoutRef.current) {
          clearTimeout(statusCheckTimeoutRef.current);
          statusCheckTimeoutRef.current = null;
        }
        lastProcessedStatusRef.current = null;
        return;
      }

      // Reset processed status ref when polling starts
      lastProcessedStatusRef.current = null;

      // start interval
      if (!pollingIntervalRef.current) {
        pollingIntervalRef.current = setInterval(() => {
          refetchStatus();
        }, pollingInterval);
      }

      // cleanup when isPolling becomes false or component unmounts
      return () => {
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
        if (statusCheckTimeoutRef.current) {
          clearTimeout(statusCheckTimeoutRef.current);
          statusCheckTimeoutRef.current = null;
        }
      };
    }, [isPolling, productionId, pollingInterval, refetchStatus]);

    // ------- Manage isOperationInProgress: from execute start to generateView update -------
    // Set to true when mutation is pending (execute starts) or succeeds
    useEffect(() => {
      if (
        executeGenerateMutation.isPending ||
        executePlaceholderingMutation.isPending ||
        executeGenerateMutation.isSuccess ||
        executePlaceholderingMutation.isSuccess
      ) {
        setIsOperationInProgress(true);
      }
    }, [
      executeGenerateMutation.isPending,
      executePlaceholderingMutation.isPending,
      executeGenerateMutation.isSuccess,
      executePlaceholderingMutation.isSuccess,
    ]);

    // Set to false when generateView updates after polling stops
    // generateView is enabled when !isPolling, so it refetches when polling stops
    useEffect(() => {
      if (isOperationInProgress && !isPolling && generateViewData) {
        // Small delay to ensure generateView has fully updated
        const timer = setTimeout(() => {
          setIsOperationInProgress(false);
        }, GENERATE_VIEW_UPDATE_DELAY_MS);
        return () => clearTimeout(timer);
      }
    }, [isOperationInProgress, isPolling, generateViewData]);

    // ------- Stop polling when status says so (single effect) -------
    useEffect(() => {
      // Clear any pending timeout
      /* istanbul ignore next */
      if (statusCheckTimeoutRef.current) {
        clearTimeout(statusCheckTimeoutRef.current);
        statusCheckTimeoutRef.current = null;
      }

      if (!isPolling) return;

      // Add delay to ensure we get the latest data from React Query cache
      statusCheckTimeoutRef.current = setTimeout(() => {
        // Read status directly from statusData inside timeout to get latest value
        const latestStatus = statusData?.productionCurrentStatus;
        if (!latestStatus) return;

        // Create a unique key to avoid processing duplicates
        const statusKey = `${latestStatus.productionStatus}-${latestStatus.productionState}-${latestStatus.shouldContinue}-${latestStatus.isMarkComplete}`;

        // Skip if we've already processed this exact status
        /* istanbul ignore next */
        if (lastProcessedStatusRef.current === statusKey) {
          return;
        }

        const shouldStop =
          latestStatus.productionStatus === ProductionStatus.FAILED ||
          latestStatus.productionStatus === ProductionStatus.COMPLETED ||
          latestStatus.productionState === ProductionState.QCCOMPLETE ||
          latestStatus.productionState === ProductionState.CANCELCOMPLETED ||
          latestStatus.productionState === ProductionState.CANCELFAILED ||
          latestStatus.productionState === ProductionStatus.DRAFT ||
          latestStatus.shouldContinue === true;

        if (shouldStop) {
          // Mark as processed before any operations
          lastProcessedStatusRef.current = statusKey;

          // clear interval
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }
          setIsPolling(false);

          // if QC complete then advance
          if (latestStatus.isMarkComplete) {
            goNext?.();
          }
        }
      }, STATUS_CHECK_DELAY_MS);

      // Cleanup timeout on unmount or dependency change
      return () => {
        /* istanbul ignore next */
        if (statusCheckTimeoutRef.current) {
          clearTimeout(statusCheckTimeoutRef.current);
          statusCheckTimeoutRef.current = null;
        }
      };
    }, [statusData, isPolling, goNext]);

    // cleanup on unmount
    useEffect(() => {
      return () => {
        /* istanbul ignore next */
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
        }
        /* istanbul ignore next */
        if (statusCheckTimeoutRef.current) {
          clearTimeout(statusCheckTimeoutRef.current);
        }
      };
    }, []);

    const validStatesToProceed = [
      ProductionState.UNCOMMITED,
      ProductionState.QCCOMPLETE,
      ProductionState.UNCOMMITINPROGRESS,
      ProductionState.COMMITINPROGRESS,
      ProductionState.COMPLETED,
    ];

    const isValidStateForNext = validStatesToProceed.includes(
      productionGenerateDetails?.productionStateCode as ProductionState
    );

    const isNextDisabled =
      isSubmitting ||
      !isValid ||
      executeGenerateMutation.isPending ||
      executePlaceholderingMutation.isPending ||
      !isValidStateForNext;

    // Track from execute start to generateView update
    const isAnyOperationInProgress =
      isOperationInProgress ||
      executeGenerateMutation.isPending ||
      executePlaceholderingMutation.isPending ||
      isPolling;

    // Report state changes to parent
    useEffect(() => {
      onStateChange?.({
        saveDisabled: true,
        nextDisabled: isNextDisabled,
      });
    }, [isNextDisabled, onStateChange]);

    // helper to build minimal payload for executeGenerate
    const buildExecutePayload = (
      overrides?: Partial<ExecuteGenerateRequestBody>
    ) => {
      const d = productionGenerateDetails;
      return {
        productionId: productionId,
        moduleType: d?.moduleType || moduleType,
        isMarkComplete: d?.isMarkComplete || false,
        shouldContinue: d?.shouldContinue || false,
        canGenerate: d?.canGenerate || false,
        canRegenerate: d?.canRegenerate || false,
        securityGroupIdForProduction:
          d?.securityGroupIdForProduction || securityGroupId,
        productionType: d?.productionType || ProductionType.GENERATE,
        productionStateCode: d?.productionStateCode || '',
        productionTempararyFolderPath: d?.productionTempararyFolderPath || '',
        productionDestinationFolderPath:
          d?.productionDestinationFolderPath || '',
        productionFolderPath: d?.productionFolderPath || '',
        shouldPerformBlankPageDetection:
          d?.shouldPerformBlankPageDetection || false,
        regenerateType: d?.regenerateType ?? RegenerateType.RegenerateAll,
        ...(overrides || {}),
      };
    };

    const buildPlaceholderingPayload = (): ExecutePlaceholderingRequestBody => {
      const d = productionGenerateDetails;
      return {
        productionId: productionId,
        moduleType: d?.moduleType || moduleType,
        isMarkComplete: d?.isMarkComplete || false,
        shouldContinue: d?.shouldContinue || false,
        canGenerate: d?.canGenerate || false,
        canRegenerate: d?.canRegenerate || false,
        securityGroupIdForProduction:
          d?.securityGroupIdForProduction || securityGroupId,
        productionType: d?.productionType || ProductionType.GENERATE,
        productionStateCode: d?.productionStateCode || '',
        productionTempararyFolderPath: d?.productionTempararyFolderPath || '',
        productionDestinationFolderPath:
          d?.productionDestinationFolderPath || '',
        productionFolderPath: d?.productionFolderPath || '',
        shouldPerformBlankPageDetection:
          d?.shouldPerformBlankPageDetection || false,
        regenerateType: d?.regenerateType ?? RegenerateType.RegenerateAll,
      };
    };

    const handleGenerate = () => {
      if (!productionGenerateDetails) return;
      const payload = buildExecutePayload();
      executeGenerateMutation.mutate(payload);
    };

    const handleRegenerate = () => {
      setShowRegenerateModal(true);
    };

    const handleRegenerateModalClose = () => {
      setShowRegenerateModal(false);
    };

    const handleRegenerateModalContinue = () => {
      if (!productionGenerateDetails) return;
      setShowRegenerateModal(false);
      const payload = buildExecutePayload({
        regenerateType: selectedRegenerateType,
      });
      executeGenerateMutation.mutate(payload);
    };

    const handlePlaceholderErrorDocsClick = () => {
      setShowPlaceholderErrorDocsModal(true);
    };

    const handlePlaceholderErrorDocsModalClose = () => {
      setShowPlaceholderErrorDocsModal(false);
    };

    const handlePlaceholderErrorDocsModalContinue = () => {
      if (!productionGenerateDetails) return;
      setShowPlaceholderErrorDocsModal(false);
      const payload = buildPlaceholderingPayload();
      executePlaceholderingMutation.mutate(payload);
    };

    const exportBatesParams: GetExportBatesRangeRequestParam = {
      productionId,
    };

    const {refetch: refetchExportBatesRange, isFetching: isExportingBates} =
      useGetExportBatesRange(exportBatesParams, false);

    const handleExportBates = async () => {
      const {error} = await refetchExportBatesRange();
      if (error) {
        getStatus?.(true, ToastType.Error, error?.message || '');
      } else {
        getStatus?.(
          true,
          ToastType.Success,
          t(
            'productionsAndExports.createNewProduction.generate.exportBatesSuccessMessage'
          )
        );
      }
    };

    const onSubmit = (data: ProductionGenerateFormData) => {
      if (data.action === FormAction.NEXT) {
        goNext?.();
      }
    };

    return (
      <>
        <Box
          className="new-production-form-section"
          component="form"
          onSubmit={handleSubmit(onSubmit)}
          role="form"
          aria-label={t(
            'productionsAndExports.createNewProduction.generate.headerText'
          )}
        >
          <Box className="new-production-form generate">
            <Box className="new-production-form-header">
              <Typography
                id="generate-header"
                className="typo-text subhead2_size"
                data-testid={`generate-header-text`}
              >
                {t(
                  'productionsAndExports.createNewProduction.generate.headerText'
                )}
              </Typography>
            </Box>
            <Box
              className="new-production-form-body"
              role="group"
              aria-labelledby="generate-header"
            >
              {/* Production Info Section */}
              <Box className="generate-info-section">
                <Box className="generate-info-content">
                  <Typography
                    className="generate-production-name typo-text subhead1_size"
                    data-testid={`generate-production-name`}
                  >
                    {productionGenerateDetails?.productionName || ''}
                  </Typography>
                  <Box className="generate-info-details">
                    <Typography
                      className="generate-info-label typo-text label1_size"
                      data-testid={`generate-status-label`}
                    >
                      {t(
                        'productionsAndExports.createNewProduction.generate.statusLabel'
                      )}
                    </Typography>
                    <Typography
                      className="generate-info-value typo-text label1_size"
                      data-testid={`generate-typography-parsed-status`}
                    >
                      {parsedStatus ? (
                        <>
                          {parsedStatus.statusCode ===
                          ProductionStatus.FAILED ? (
                            <>
                              {parsedStatus.mainText}:{' '}
                              <ArrButton
                                className="generate-status-button generate-status-button--failed"
                                onClick={() => {
                                  setOpenErrorGridModal(true);
                                  setGridType(ErrorGridType.ProductionErrors);
                                }}
                                dataTestId="parsed-status"
                              >
                                {parsedStatus.status}
                              </ArrButton>
                            </>
                          ) : parsedStatus.mainText ===
                            t('cancelProduction.cancel') ? (
                            <span
                              className="generate-status-button--cancel-in-progress"
                              data-testid={`generate-status-mainText`}
                            >
                              {parsedStatus.mainText} {parsedStatus.status}
                            </span>
                          ) : (
                            <>
                              {parsedStatus.mainText}
                              {parsedStatus.status && (
                                <>
                                  :{' '}
                                  <span
                                    className={`${
                                      parsedStatus.statusCode ===
                                      ProductionStatus.COMPLETED
                                        ? 'text-success'
                                        : ''
                                    }`}
                                    data-testid={`generate-span-text`}
                                  >
                                    {parsedStatus.status}
                                  </span>
                                </>
                              )}
                            </>
                          )}
                        </>
                      ) : (
                        displayStatus
                      )}

                      <span
                        className="generate-status-count"
                        data-testid={`generate-span-production-docs-complete`}
                      >
                        {producitonDocsCompleteText &&
                          ` ${producitonDocsCompleteText}`}
                      </span>
                    </Typography>
                    <Typography
                      className="generate-info-label typo-text label1_size"
                      data-testid={`generate-bates-range-label`}
                    >
                      {t(
                        'productionsAndExports.createNewProduction.generate.batesRangeLabel'
                      )}
                    </Typography>
                    <Typography
                      className="generate-info-value typo-text label1_size"
                      data-testid={`generate-typography-bates-range`}
                    >
                      {batesRange}
                    </Typography>
                  </Box>
                </Box>
                <Box className="generate-button-container">
                  {isAnyOperationInProgress ? (
                    <>
                      <ArrButton
                        variant="primary"
                        size="medium"
                        disabled={isAnyOperationInProgress}
                        dataTestId="generate-in-progress-button"
                      >
                        {t(
                          'productionsAndExports.createNewProduction.generate.modals.regenerate.inProgress'
                        )}
                      </ArrButton>
                    </>
                  ) : (
                    <>
                      {canShowPlaceholderErrorDocsButton && (
                        <ArrButton
                          variant="secondary"
                          size="medium"
                          onClick={handlePlaceholderErrorDocsClick}
                          dataTestId="placeholder-error-docs-button"
                        >
                          {t(
                            'productionsAndExports.createNewProduction.generate.placeholderErrorDocsButton'
                          )}
                        </ArrButton>
                      )}
                      {canShowGenerateButton && (
                        <ArrButton
                          variant="primary"
                          size="medium"
                          onClick={handleGenerate}
                          dataTestId="generate-button"
                        >
                          {t(
                            'productionsAndExports.createNewProduction.generate.generateButton'
                          )}
                        </ArrButton>
                      )}
                      {shouldContinueGeneration && (
                        <ArrButton
                          variant="primary"
                          size="medium"
                          onClick={handleGenerate}
                          dataTestId="generate-continue-button"
                        >
                          {t(
                            'productionsAndExports.createNewProduction.generate.continueGenerationButton'
                          )}
                        </ArrButton>
                      )}
                      {canShowRegenerateButton && (
                        <ArrButton
                          variant="primary"
                          size="medium"
                          onClick={() => {
                            setSelectedRegenerateType(
                              RegenerateType.RegenerateAll
                            );
                            handleRegenerate();
                          }}
                          disabled={isInProgressOrCompleted}
                          dataTestId="regenerate-button"
                        >
                          {t(
                            'productionsAndExports.createNewProduction.generate.regenerateButton'
                          )}
                        </ArrButton>
                      )}
                    </>
                  )}
                </Box>
              </Box>

              {/* Automated Check Section */}
              <Box className="generate-automated-check-section">
                <Box className="automated-check-buttons">
                  <ArrButton
                    variant="secondary"
                    size="medium"
                    onClick={handleExportBates}
                    disabled={!canShowExportBatesButton || isExportingBates}
                    dataTestId="export-bates-button"
                  >
                    {t(
                      'productionsAndExports.createNewProduction.generate.exportBatesButton'
                    )}
                  </ArrButton>
                </Box>
                <Box className="automated-check-table">
                  <Box className="automated-check-table-header">
                    <Box className="table-row">
                      <Box className="table-cell">
                        <Typography
                          className="typo-text label2_size"
                          data-testid={`generate-automated-check-label`}
                        >
                          {t(
                            'productionsAndExports.createNewProduction.generate.automatedCheckLabel'
                          )}
                        </Typography>
                      </Box>
                      <Box className="table-cell">
                        <Typography
                          className="typo-text label2_size"
                          data-testid={`generate-status-table-header`}
                        >
                          {t(
                            'productionsAndExports.createNewProduction.generate.statusTableHeader'
                          )}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                  <Box className="automated-check-table-body">
                    {automatedChecks.length > 0 &&
                      automatedChecks.map((check, index) => (
                        <Box className="table-row" key={index}>
                          <Box className="table-cell">
                            <Typography
                              className="typo-text body3_size"
                              data-testid={`generate-typography-check-name-${index}`}
                            >
                              {check.name}
                            </Typography>
                          </Box>
                          <Box className="table-cell">
                            <Typography
                              className={`typo-text body3_size ${check.className || ''}`}
                              data-testid={`generate-typography-check-status-${index}`}
                            >
                              {check.status || ''}
                            </Typography>
                          </Box>
                        </Box>
                      ))}
                  </Box>
                </Box>
              </Box>

              {canShowNativelyPlaceholderErrorDocs && (
                <Box className="generate-bottom-section">
                  <ArrButton
                    variant="text"
                    size="medium"
                    onClick={() => {
                      setOpenErrorGridModal(true);
                      setGridType(ErrorGridType.PlaceHolderErrors);
                    }}
                    dataTestId="natively-placeholder-error-docs-button"
                  >
                    {t(
                      'productionsAndExports.createNewProduction.generate.viewNativelyPlaceholderErrorDocs',
                      {
                        count:
                          productionGenerateDetails?.nativelyGeneratedDocs || 0,
                      }
                    )}
                  </ArrButton>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
        {/* Regenerate Production Modal */}
        <ArrModal
          showModal={showRegenerateModal}
          showClose={false}
          title={t(
            'productionsAndExports.createNewProduction.generate.modals.regenerate.title'
          )}
          description={t(
            'productionsAndExports.createNewProduction.generate.modals.regenerate.description'
          )}
          onClose={handleRegenerateModalClose}
          className="regenerate-production-modal"
          dataTestId="regenerate-production-modal"
          buttons={[
            {
              id: 'cancel',
              label: t(
                'productionsAndExports.createNewProduction.generate.modals.regenerate.cancel'
              ),
              variant: 'secondary',
              onClick: handleRegenerateModalClose,
              dataTestId: 'rpm-cancel-btn',
            },
            {
              id: 'continue',
              label: t(
                'productionsAndExports.createNewProduction.generate.modals.regenerate.continue'
              ),
              dataTestId: 'rpm-continue-btn',
              variant: 'primary',
              onClick: handleRegenerateModalContinue,
              disabled: executeGenerateMutation.isPending,
              loading: executeGenerateMutation.isPending,
            },
          ]}
        >
          <Box className="regenerate-modal-content">
            {/* Regeneration Options Radio Buttons */}
            <Box className="regenerate-options-section">
              <RadioGroup
                data-testid="regenerate-options-radio-group"
                value={selectedRegenerateType}
                onChange={(e) => {
                  setSelectedRegenerateType(
                    Number(e.target.value) as RegenerateType
                  );
                }}
                className="custom-radio-group"
                aria-label={t(
                  'productionsAndExports.createNewProduction.generate.modals.regenerate.title'
                )}
              >
                <FormControlLabel
                  data-testid={`regenerate-options-section${RegenerateType.RegenerateAll}`}
                  value={RegenerateType.RegenerateAll}
                  control={
                    <Radio
                      icon={<RadioEnableIcon />}
                      checkedIcon={<RadioActiveIcon />}
                      className="typo-text label2_size radio-btn"
                      slotProps={{
                        input: {
                          'aria-label': t(
                            'productionsAndExports.createNewProduction.generate.modals.regenerate.option1.label'
                          ),
                          'data-testid': `radio-${RegenerateType.RegenerateAll}`,
                        } as React.InputHTMLAttributes<HTMLInputElement>,
                      }}
                    />
                  }
                  className="typo-text label2_size radio-btn-container"
                  label={
                    <span
                      className="typo-text label2_size radio-text"
                      data-testid={`generate-label`}
                    >
                      {t(
                        'productionsAndExports.createNewProduction.generate.modals.regenerate.option1.label'
                      )}
                    </span>
                  }
                />
                <FormControlLabel
                  data-testid={`regenerate-options-errors-${RegenerateType.RegenerateOnlyErrors}`}
                  value={RegenerateType.RegenerateOnlyErrors}
                  control={
                    <Radio
                      icon={<RadioEnableIcon />}
                      checkedIcon={<RadioActiveIcon />}
                      className="typo-text label2_size radio-btn"
                      slotProps={{
                        input: {
                          'aria-label': t(
                            'productionsAndExports.createNewProduction.generate.modals.regenerate.option2.label'
                          ),
                          'data-testid': `radio-${RegenerateType.RegenerateOnlyErrors}`,
                        } as React.InputHTMLAttributes<HTMLInputElement>,
                      }}
                    />
                  }
                  className="typo-text label2_size radio-btn-container"
                  label={
                    <span
                      className="typo-text label2_size radio-text"
                      data-testid={`generate-label-text`}
                    >
                      {t(
                        'productionsAndExports.createNewProduction.generate.modals.regenerate.option2.label'
                      )}
                    </span>
                  }
                />
              </RadioGroup>
            </Box>
          </Box>
        </ArrModal>
        {/* Placeholder Error Docs Modal */}
        <ArrModal
          showModal={showPlaceholderErrorDocsModal}
          showClose={false}
          title={t(
            'productionsAndExports.createNewProduction.generate.modals.placeholderErrorDocs.title'
          )}
          description={t(
            'productionsAndExports.createNewProduction.generate.modals.placeholderErrorDocs.description'
          )}
          onClose={handlePlaceholderErrorDocsModalClose}
          className="placeholder-error-docs-modal"
          dataTestId="placeholder-error-docs-modal"
          buttons={[
            {
              id: 'cancel',
              label: t(
                'productionsAndExports.createNewProduction.generate.modals.placeholderErrorDocs.cancel'
              ),
              variant: 'secondary',
              onClick: handlePlaceholderErrorDocsModalClose,
              dataTestId: 'ped-cancel-btn',
            },
            {
              id: 'continue',
              label: t(
                'productionsAndExports.createNewProduction.generate.modals.placeholderErrorDocs.continue'
              ),
              dataTestId: 'ped-continue-btn',
              variant: 'primary',
              onClick: handlePlaceholderErrorDocsModalContinue,
              disabled: executePlaceholderingMutation.isPending,
              loading: executePlaceholderingMutation.isPending,
            },
          ]}
        />
        {openErrorGridModal && (
          <ErrorGridModal
            productionId={productionId}
            securityGroupId={securityGroupId}
            open={openErrorGridModal}
            onClose={() => setOpenErrorGridModal(false)}
            title={
              isProductionSet
                ? t(
                    'productionsAndExports.createNewProduction.generate.modals.errors.productionErrors'
                  )
                : t(
                    'productionsAndExports.createNewProduction.generate.modals.errors.exportErrors'
                  )
            }
            tooltip={t(
              'productionsAndExports.createNewProduction.generate.modals.errors.tooltip'
            )}
            gridType={gridType}
          />
        )}
      </>
    );
  }
);

Generate.displayName = 'Generate';

export default Generate;
